'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
import { AIFI_CHART_PRIMARY_COLOR, AIFI_CHART_SECONDARY_COLOR, AIFI_CHART_WITHOUT_ANIMATION, AIFI_LINE_CHART_OPTIONS } from '@/domains/pages/ai-proficiency/constants';
import { orgLinePoints, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS, ringHoverPoint, smallSolidHoverPoint } from '@/domains/pages/ai-proficiency/utils/chart-hover-points';
import { buildPerTaskSpendOverTimeTooltipHtml, buildTotalSpendOverTimeTooltipHtml } from '@/domains/pages/ai-spend/components/spend-chart-tooltip';
import type { SpendChartMode } from '@/domains/pages/ai-spend/constants';
import { computeTotalSpendAxisBounds } from '@/domains/pages/ai-spend/utils/ai-spend-over-time.utils';
import type { SpendPeerBandPoint } from '@/domains/pages/ai-spend/utils/map-ai-spend-segment-trend';
import { applyVerticalBarGradientFills, verticalBarGradientPlugin } from '@/domains/pages/ai-spend/utils/bar-fill';
import { chartIndexCrosshairPlugin } from '@/domains/pages/shared/chart-crosshair';
import { computePaddedYAxisBounds } from '@/domains/pages/shared/chart-axis';
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { TIME_SERIES_INTERACTION } from '@/domains/pages/shared/chart-interaction';
import { themeColors } from '@/theme/palette';
import { jetbrainsMonoChartFontFamily } from '@/theme/utils/font.utils';
import { formatSpendAxisTick } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { BarController, BarElement, CategoryScale, Chart as ChartJS, ChartData, ChartOptions, Filler, LinearScale, LineController, LineElement, PointElement } from 'chart.js';
import { useEffect, useMemo, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
ChartJS.register(BarController, BarElement, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, chartIndexCrosshairPlugin, verticalBarGradientPlugin);
type SpendOverTimeChartBaseProps = {
  labels: string[];
  totalSpend: Array<number | null>;
  spendPerTask: Array<number | null>;
  peerBand?: SpendPeerBandPoint[];
  seriesLabel?: string;
  avgSpendPerTask?: Array<number | null>;
  avgLineLabel?: string;
  segmentNoun?: string;
};
const SERIES_COLOR = AIFI_CHART_PRIMARY_COLOR;
const BENCHMARK_BAND_FILL_COLOR = `${AIFI_CHART_SECONDARY_COLOR}1A`;
const BENCHMARK_BAND_EDGE_COLOR = AIFI_CHART_SECONDARY_COLOR;
const SEGMENT_AVG_LINE_COLOR = 'rgba(255, 255, 255, 0.45)';
const TOTAL_SPEND_TOOLTIP_ID = 'ai-spend-total-over-time-tooltip';
const PER_TASK_TOOLTIP_ID = 'ai-spend-per-task-over-time-tooltip';
const HIDDEN_DATALABELS = {
  datalabels: {
    display: false
  }
};
function collectTotalSpendAxisValues(totalSpend: Array<number | null>): number[] {
  return totalSpend.filter((value): value is number => value != null);
}
function collectPerTaskAxisValues(spendPerTask: Array<number | null>, peerBand?: SpendPeerBandPoint[], avgSpendPerTask?: Array<number | null>): number[] {
  const seriesValues = spendPerTask.filter((value): value is number => value != null);
  const avgValues = (avgSpendPerTask ?? []).filter((value): value is number => value != null);
  if (!peerBand?.length) {
    return [...seriesValues, ...avgValues];
  }
  return [...seriesValues, ...avgValues, ...peerBand.flatMap(point => [point.cheapest, point.mostExpensive].filter((value): value is number => value != null))];
}
function buildSharedXScaleOptions() {
  return {
    x: {
      grid: {
        display: false
      },
      border: {
        display: false
      },
      ticks: {
        color: themeColors.text.secondary,
        font: {
          size: 11
        },
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12
      }
    }
  };
}
export function TotalSpendOverTimeChart(t0) {
  const $ = _c(38);
  const {
    labels,
    totalSpend
  } = t0;
  const chartRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const frame = requestAnimationFrame(() => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }
        applyVerticalBarGradientFills(chart, SERIES_COLOR);
        chart.draw();
      });
      return () => cancelAnimationFrame(frame);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== labels || $[3] !== totalSpend) {
    t3 = [labels, totalSpend];
    $[2] = labels;
    $[3] = totalSpend;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const hasData = labels.length > 0 && totalSpend.some(_temp3);
  let t4;
  if ($[5] !== totalSpend) {
    t4 = computeTotalSpendAxisBounds(collectTotalSpendAxisValues(totalSpend));
    $[5] = totalSpend;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const yAxisBounds = t4;
  let t5;
  if ($[7] !== totalSpend) {
    t5 = [{
      type: "bar" as const,
      label: "Total spend",
      data: totalSpend,
      backgroundColor: SERIES_COLOR,
      borderColor: SERIES_COLOR,
      borderWidth: 1,
      borderRadius: 3,
      order: 1,
      yAxisID: "y"
    }];
    $[7] = totalSpend;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== labels || $[10] !== t5) {
    t6 = {
      labels,
      datasets: t5
    };
    $[9] = labels;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const chartData = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: {
        top: 4,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t8 = buildSharedXScaleOptions();
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t10;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      display: true,
      color: "rgba(255, 255, 255, 0.12)"
    };
    t10 = {
      color: "rgba(255, 255, 255, 0.08)",
      tickLength: 4
    };
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      size: 11,
      family: jetbrainsMonoChartFontFamily
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== yAxisBounds.stepSize) {
    t12 = value_0 => formatSpendAxisTick(Number(value_0), yAxisBounds.stepSize);
    $[17] = yAxisBounds.stepSize;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== yAxisBounds.stepSize) {
    t13 = {
      display: true,
      color: themeColors.text.secondary,
      font: t11,
      padding: 6,
      stepSize: yAxisBounds.stepSize,
      callback: t12
    };
    $[19] = t12;
    $[20] = yAxisBounds.stepSize;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== yAxisBounds.max || $[24] !== yAxisBounds.min) {
    t14 = {
      ...t8,
      y: {
        position: "left",
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        border: t9,
        grid: t10,
        ticks: t13
      }
    };
    $[22] = t13;
    $[23] = yAxisBounds.max;
    $[24] = yAxisBounds.min;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      display: false
    };
    t16 = {
      color: SERIES_COLOR
    };
    $[26] = t15;
    $[27] = t16;
  } else {
    t15 = $[26];
    t16 = $[27];
  }
  let t17;
  if ($[28] !== labels || $[29] !== totalSpend) {
    t17 = {
      ...HIDDEN_DATALABELS,
      legend: t15,
      verticalBarGradient: t16,
      tooltip: {
        enabled: false,
        external(context) {
          let tooltipEl = document.getElementById(TOTAL_SPEND_TOOLTIP_ID);
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = TOTAL_SPEND_TOOLTIP_ID;
          }
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }
          const index = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
          tooltipEl.innerHTML = buildTotalSpendOverTimeTooltipHtml({
            header: labels[index] ?? "",
            totalSpend: totalSpend[index] ?? null
          });
          positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
        }
      }
    };
    $[28] = labels;
    $[29] = totalSpend;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t14 || $[32] !== t17) {
    t18 = {
      ...AIFI_CHART_WITHOUT_ANIMATION,
      responsive: true,
      maintainAspectRatio: false,
      interaction: TIME_SERIES_INTERACTION,
      hover: TIME_SERIES_INTERACTION,
      layout: t7,
      scales: t14,
      plugins: t17
    };
    $[31] = t14;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const options = t18;
  if (!hasData) {
    let t19;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="spend-over-time-chart-total"><ChartNoDataEmptyState className="h-full" /></div>;
      $[34] = t19;
    } else {
      t19 = $[34];
    }
    return t19;
  }
  let t19;
  if ($[35] !== chartData || $[36] !== options) {
    t19 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="spend-over-time-chart-total"><Bar ref={chartRef} data={chartData} options={options} /></div>;
    $[35] = chartData;
    $[36] = options;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
}
function _temp3(value) {
  return value != null;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.getElementById(TOTAL_SPEND_TOOLTIP_ID)?.remove();
}
export function SpendPerTaskOverTimeChart(t0) {
  const $ = _c(55);
  const {
    labels,
    spendPerTask,
    peerBand,
    seriesLabel: t1,
    avgSpendPerTask,
    avgLineLabel,
    segmentNoun
  } = t0;
  const seriesLabel = t1 === undefined ? "AI spend per task" : t1;
  const chartRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useEffect(_temp5, t2);
  const hasData = labels.length > 0 && spendPerTask.some(_temp6);
  const hasPeerBand = (peerBand?.length ?? 0) > 0;
  let t3;
  if ($[1] !== avgSpendPerTask) {
    t3 = avgSpendPerTask != null && avgSpendPerTask.some(_temp7);
    $[1] = avgSpendPerTask;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const hasAvgLine = t3;
  let t4;
  if ($[3] !== avgSpendPerTask || $[4] !== peerBand || $[5] !== spendPerTask) {
    t4 = computePaddedYAxisBounds(collectPerTaskAxisValues(spendPerTask, peerBand, avgSpendPerTask), {
      clampMax: Number.POSITIVE_INFINITY
    });
    $[3] = avgSpendPerTask;
    $[4] = peerBand;
    $[5] = spendPerTask;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const yAxisBounds = t4;
  let t5;
  if ($[7] !== hasPeerBand || $[8] !== peerBand) {
    t5 = hasPeerBand ? [{
      label: "Cheapest",
      data: peerBand.map(_temp8),
      borderColor: "transparent",
      backgroundColor: BENCHMARK_BAND_FILL_COLOR,
      fill: "+1",
      tension: 0.35,
      spanGaps: true,
      order: 4,
      ...smallSolidHoverPoint(BENCHMARK_BAND_EDGE_COLOR)
    }, {
      label: "Most expensive",
      data: peerBand.map(_temp9),
      borderColor: "transparent",
      backgroundColor: BENCHMARK_BAND_FILL_COLOR,
      fill: false,
      tension: 0.35,
      spanGaps: true,
      order: 3,
      ...smallSolidHoverPoint(BENCHMARK_BAND_EDGE_COLOR)
    }] : [];
    $[7] = hasPeerBand;
    $[8] = peerBand;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const peerDatasets = t5;
  let t6;
  if ($[10] !== avgLineLabel || $[11] !== avgSpendPerTask || $[12] !== hasAvgLine) {
    t6 = hasAvgLine && avgSpendPerTask ? [{
      label: avgLineLabel ?? "Average",
      data: avgSpendPerTask,
      borderColor: SEGMENT_AVG_LINE_COLOR,
      borderDash: [4, 4],
      borderWidth: 1.5,
      fill: false,
      tension: 0.35,
      spanGaps: false,
      order: 2,
      ...ringHoverPoint(SEGMENT_AVG_LINE_COLOR, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS)
    }] : [];
    $[10] = avgLineLabel;
    $[11] = avgSpendPerTask;
    $[12] = hasAvgLine;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const avgLineDataset = t6;
  let t7;
  if ($[14] !== spendPerTask) {
    t7 = orgLinePoints(SERIES_COLOR, spendPerTask);
    $[14] = spendPerTask;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== seriesLabel || $[17] !== spendPerTask || $[18] !== t7) {
    t8 = {
      label: seriesLabel,
      data: spendPerTask,
      borderColor: SERIES_COLOR,
      backgroundColor: SERIES_COLOR,
      borderWidth: 2,
      fill: false,
      tension: 0.35,
      spanGaps: false,
      order: 1,
      ...t7
    };
    $[16] = seriesLabel;
    $[17] = spendPerTask;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== avgLineDataset || $[21] !== peerDatasets || $[22] !== t8) {
    t9 = [...peerDatasets, ...avgLineDataset, t8];
    $[20] = avgLineDataset;
    $[21] = peerDatasets;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== labels || $[25] !== t9) {
    t10 = {
      labels,
      datasets: t9
    };
    $[24] = labels;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const chartData = t10;
  let t11;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      padding: {
        top: 4,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t12 = buildSharedXScaleOptions();
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  let t13;
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      display: true,
      color: "rgba(255, 255, 255, 0.12)"
    };
    t14 = {
      color: "rgba(255, 255, 255, 0.08)",
      tickLength: 4
    };
    $[29] = t13;
    $[30] = t14;
  } else {
    t13 = $[29];
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      size: 11,
      family: jetbrainsMonoChartFontFamily
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== yAxisBounds.stepSize) {
    t16 = value_1 => formatSpendAxisTick(Number(value_1), yAxisBounds.stepSize);
    $[32] = yAxisBounds.stepSize;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16 || $[35] !== yAxisBounds.stepSize) {
    t17 = {
      display: true,
      color: themeColors.text.secondary,
      font: t15,
      padding: 6,
      stepSize: yAxisBounds.stepSize,
      callback: t16
    };
    $[34] = t16;
    $[35] = yAxisBounds.stepSize;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17 || $[38] !== yAxisBounds.max || $[39] !== yAxisBounds.min) {
    t18 = {
      ...t12,
      y: {
        position: "left",
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        border: t13,
        grid: t14,
        ticks: t17
      }
    };
    $[37] = t17;
    $[38] = yAxisBounds.max;
    $[39] = yAxisBounds.min;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      display: false
    };
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== avgLineLabel || $[43] !== avgSpendPerTask || $[44] !== labels || $[45] !== seriesLabel || $[46] !== spendPerTask) {
    t20 = {
      ...HIDDEN_DATALABELS,
      legend: t19,
      tooltip: {
        enabled: false,
        external(context) {
          let tooltipEl = document.getElementById(PER_TASK_TOOLTIP_ID);
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = PER_TASK_TOOLTIP_ID;
          }
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }
          const index = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
          tooltipEl.innerHTML = buildPerTaskSpendOverTimeTooltipHtml({
            header: labels[index] ?? "",
            spendPerTask: spendPerTask[index] ?? null,
            seriesLabel,
            avgSpendPerTask: avgSpendPerTask?.[index] ?? null,
            avgLineLabel
          });
          positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
        }
      }
    };
    $[42] = avgLineLabel;
    $[43] = avgSpendPerTask;
    $[44] = labels;
    $[45] = seriesLabel;
    $[46] = spendPerTask;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t18 || $[49] !== t20) {
    t21 = {
      ...AIFI_LINE_CHART_OPTIONS,
      responsive: true,
      maintainAspectRatio: false,
      interaction: TIME_SERIES_INTERACTION,
      hover: TIME_SERIES_INTERACTION,
      layout: t11,
      scales: t18,
      plugins: t20
    };
    $[48] = t18;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const options = t21;
  if (!hasData) {
    let t22;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="spend-over-time-chart-per-task"><ChartNoDataEmptyState className="h-full" /></div>;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    return t22;
  }
  let t22;
  if ($[52] !== chartData || $[53] !== options) {
    t22 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="spend-over-time-chart-per-task"><Line ref={chartRef} data={chartData} options={options} /></div>;
    $[52] = chartData;
    $[53] = options;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}
function _temp9(point_0) {
  return point_0.mostExpensive;
}
function _temp8(point) {
  return point.cheapest;
}
function _temp7(value_0) {
  return value_0 != null;
}
function _temp6(value) {
  return value != null;
}
function _temp5() {
  return _temp4;
}
function _temp4() {
  document.getElementById(PER_TASK_TOOLTIP_ID)?.remove();
}
type SpendOverTimeLegendProps = {
  mode: SpendChartMode;
  peerBand?: SpendPeerBandPoint[];
  segmentNoun?: string;
  seriesLabel?: string;
  avgLineLabel?: string;
  className?: string;
};
export function SpendOverTimeLegend(t0) {
  const $ = _c(15);
  const {
    mode,
    peerBand,
    segmentNoun,
    seriesLabel: t1,
    avgLineLabel,
    className
  } = t0;
  const seriesLabel = t1 === undefined ? "AI Spend per task" : t1;
  if (mode === "total") {
    return null;
  }
  const hasPeerBand = (peerBand?.length ?? 0) > 0;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-wrap items-center gap-4 text-xs text-muted-foreground", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="h-0.5 w-4" style={{
      backgroundColor: SERIES_COLOR
    }} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== seriesLabel) {
    t4 = <span className="flex items-center gap-2">{t3}{seriesLabel}</span>;
    $[3] = seriesLabel;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== avgLineLabel) {
    t5 = avgLineLabel ? <span className="flex items-center gap-2"><span className="h-0.5 w-4 border-t border-dashed border-white/45" />{avgLineLabel}</span> : null;
    $[5] = avgLineLabel;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== hasPeerBand || $[8] !== segmentNoun) {
    t6 = hasPeerBand ? <span className="flex items-center gap-2"><span className="size-2 rounded-sm" style={{
        backgroundColor: BENCHMARK_BAND_EDGE_COLOR
      }} />Cheapest to most expensive {segmentNoun ?? "segment"}</span> : null;
    $[7] = hasPeerBand;
    $[8] = segmentNoun;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className={t2}>{t4}{t5}{t6}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}